import { z } from '@customerio/hydra-validation';
import type { Template as ApiTemplate } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Template entity — workspace-scoped message templates.
 *
 * Minimal detail-only entity: `id` normalized to `string` (the wire ships a
 * number). The epoch `created`/`updated` timestamps are left untransformed
 * and a list+mutation surface can be added when a consumer needs them.
 */
export type Template = Omit<ApiTemplate, 'id'> & { id: string };

const TemplateFromApi = readSchema<Template>(
  z.looseObject({
    id: z.number().transform(String),
  }),
);

/** @public */
export const template = defineEntity<Template>({
  name: 'template',
  scope: 'workspace',
  pk: (t) => t.id,
  schema: { read: TemplateFromApi },
});

export const templateKeys = {
  ...template.keys,
  /**
   * The layouts a template response side-loads. Kept apart from
   * `layoutKeys.detail` because the template endpoints embed a partial layout
   * (no `is_default`, `archived`, `in_use` or `campaigns_count`), which must
   * not overwrite the full layout the layout endpoints return.
   */
  layouts: (workspaceId: number, templateId: string) =>
    [...template.keys.detail(workspaceId, templateId), 'layouts'] as const,
};

export function parseTemplate(raw: ApiTemplate): Template {
  return template.parse(raw);
}
